import React, { useState } from "react";
import { Download, ShieldCheck, Search, FileText } from "lucide-react";
import api, { API, apiError } from "@/lib/apiClient";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function DonorReceipts() {
  const [receiptNo, setReceiptNo] = useState("");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const lookup = async () => {
    setError(""); setResult(null);
    if (!receiptNo.trim() || mobile.replace(/\D/g, "").length < 10) {
      setError("Please enter your receipt number and 10-digit registered mobile number.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/receipts/lookup", { receipt_no: receiptNo.trim(), mobile: mobile.trim() });
      setResult(data);
    } catch (e) { setError(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fade-in max-w-2xl mx-auto px-6 py-16" data-testid="donor-receipts">
      <SEO route="/receipts" title="Download Your Donation Receipt — National Public Service Trust"
        description="Re-download your official National Public Service Trust donation receipt using your receipt number and registered mobile number." />
      <div className="text-center mb-8">
        <div className="label-eyebrow text-npst-gold mb-2">National Public Service Trust</div>
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-npst-ink">Download Your Receipt</h1>
        <p className="text-sm text-npst-muted mt-3">Enter your receipt number and the mobile number used while donating to securely retrieve your official receipt.</p>
      </div>

      <div className="bg-white border border-npst-line rounded-md p-6 space-y-4">
        <div>
          <label className="label-eyebrow text-slate-500 block mb-2">Receipt Number</label>
          <Input value={receiptNo} onChange={(e) => setReceiptNo(e.target.value)}
            placeholder="NPST/DR/2026-27/0001" className="font-mono" data-testid="donor-receipt-input" />
        </div>
        <div>
          <label className="label-eyebrow text-slate-500 block mb-2">Registered Mobile Number</label>
          <Input value={mobile} onChange={(e) => setMobile(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookup()}
            placeholder="10-digit mobile" inputMode="numeric" data-testid="donor-mobile-input" />
        </div>
        {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="donor-lookup-error">{error}</div>}
        <Button onClick={lookup} disabled={busy} data-testid="donor-lookup-btn"
          className="w-full bg-npst-navy hover:bg-npst-navyDeep h-11">
          <Search size={15} className="mr-2" /> {busy ? "Searching…" : "Find My Receipt"}
        </Button>
      </div>

      {result && (
        <div className="bg-white border border-npst-line rounded-md mt-6 overflow-hidden" data-testid="donor-receipt-result">
          <div className="flex items-center justify-center gap-2 py-3 border-b text-green-700 bg-green-50 border-green-200">
            <ShieldCheck size={18} /> <span className="font-semibold uppercase tracking-[0.14em] text-sm">Receipt Found</span>
          </div>
          <div className="divide-y divide-npst-line">
            {[["Receipt No.", result.receipt_no], ["Donor Name", result.name],
              ["Donation Amount", `₹${Number(result.amount).toLocaleString("en-IN")}`],
              ["Transaction Date & Time", result.date]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 px-6 py-3.5 text-sm">
                <span className="text-npst-muted uppercase tracking-wider text-xs font-semibold shrink-0">{k}</span>
                <span className="text-npst-ink font-medium text-right break-all">{v}</span>
              </div>
            ))}
          </div>
          <div className="p-6 border-t border-npst-line grid sm:grid-cols-2 gap-3">
            <a href={`${API}/verify-donation/${result.token}/receipt`} target="_blank" rel="noopener noreferrer" data-testid="donor-download-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-npst-navy text-white text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-navyDeep transition-colors"><Download size={15} /> Download Receipt</a>
            <a href={`/verify-donation/${result.token}`} target="_blank" rel="noopener noreferrer" data-testid="donor-verify-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-npst-navy text-npst-navy text-sm font-semibold uppercase tracking-[0.1em] rounded-sm hover:bg-npst-goldLight/40 transition-colors"><FileText size={15} /> View / Verify</a>
          </div>
        </div>
      )}
    </div>
  );
}
